import { useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, useOutletContext } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import CategoryNav from '@/components/menu/CategoryNav'
import CartSidebar from '@/components/menu/CartSidebar'
import { CartBottomBar } from '@/components/cart/CartBottomBar'
import MenuHeader from '@/components/menu/MenuHeader'
import MenuPage from '@/pages/menu/MenuPage'
import { StoreClosed } from '@/components/StoreClosed'
import { useAppDispatch, useAppSelector } from '@/store'
import {
	selectSelectedMenuSchedule,
	selectIsDeferred,
	selectOrderTimeUtc,
	selectSelectedOrderTypeGuid,
	selectCartItems,
	selectCalculations,
	setMenuSchedule,
	calculate,
} from '@/store/ticketSlice'
import { useScrollCompact } from '@/hooks/useScrollCompact'
import { useLocationRef } from '@/contexts/RefContext'
import { getSchedules } from '@/shared/api/timeSlots'
import { QUERY_STALE_TIMES } from '@/lib/queryConfig'
import type { LocationData, MenuSchedule } from '@/shared/api/location'
import { LayoutGrid, MainContent, StickyHeader, CartSidebarSticky } from './MenuLayout.styled'

export interface MenuLayoutContext {
	location: LocationData | undefined
	scheduleId: string
	searchTerm: string
	onClearSearch: () => void
	isStoreClosureActive?: boolean
}

export interface MenuLayoutOutletContext {
	location: LocationData
	isStoreClosureActive?: boolean
}

export default function MenuLayout() {
	const dispatch = useAppDispatch()
	const outletContext = useOutletContext<MenuLayoutOutletContext | null>()
	const [searchTerm, setSearchTerm] = useState('')
	const clearSearchRef = useRef<(() => void) | null>(null)
	const menuHeaderRef = useRef<HTMLDivElement | null>(null)

	const location = outletContext?.location
	const selectedMenuSchedule = useAppSelector(selectSelectedMenuSchedule)
	const selectedScheduleRef = selectedMenuSchedule?.ref
	// Fall back to the location's first menu schedule so the menu loads immediately
	// on first visit (before the user has gone through order settings), mirroring olo-demo behaviour.
	const scheduleId = selectedScheduleRef ?? location?.menuSchedules?.[0]?.ref ?? ''
	const isCompact = useScrollCompact(menuHeaderRef)

	const cartItems = useAppSelector(selectCartItems)
	const calculations = useAppSelector(selectCalculations)
	const selectedOrderTypeGuid = useAppSelector(selectSelectedOrderTypeGuid)

	// Ember parity (menu.js model hook lines 42–47): if no menu schedule is set in ticket state,
	// default to the first schedule from the location. This ensures menuScheduleRef is never null
	// when calculate() is called — the API rejects null with a 400.
	useEffect(() => {
		if (!selectedMenuSchedule && location?.menuSchedules?.[0]) {
			dispatch(setMenuSchedule(location.menuSchedules[0]))
		}
	}, [selectedMenuSchedule, location, dispatch])

	// On page refresh, calculations are not persisted (only cart items and order settings are).
	// If the cart has items and an order type is set, re-run calculate once so the Amount Due
	// reflects the server total immediately — matching Ember's menu route afterModel behaviour.
	const didCalculateOnMount = useRef(false)
	useEffect(() => {
		if (didCalculateOnMount.current) return
		if (calculations !== null) return
		if (cartItems.length === 0) return
		if (!selectedOrderTypeGuid) return
		if (!location) return

		didCalculateOnMount.current = true
		void dispatch(calculate())
	}, [calculations, cartItems.length, selectedOrderTypeGuid, location, dispatch])

	const ref = useLocationRef()
	const isDeferred = useAppSelector(selectIsDeferred)
	const orderTimeUtc = useAppSelector(selectOrderTimeUtc)

	const isDineIn = !selectedOrderTypeGuid || selectedOrderTypeGuid === 'DINE_IN'

	// Ember parity (menu.js getSchedules path 3): deferred non-dine-in uses availability/schedules API; ASAP/dine-in use bootstrap menuSchedules unchanged.
	const { data: availableSchedulesData } = useQuery({
		queryKey: ['available-schedules', ref, selectedOrderTypeGuid, orderTimeUtc],
		queryFn: () => getSchedules(ref, selectedOrderTypeGuid!, orderTimeUtc!),
		enabled: isDeferred && !isDineIn && !!orderTimeUtc && !!selectedOrderTypeGuid,
		staleTime: QUERY_STALE_TIMES.SHORT,
	})

	const headerSchedules = useMemo((): MenuSchedule[] | undefined => {
		if (!location) return undefined
		if (!isDeferred || isDineIn) return location.menuSchedules
		if (!availableSchedulesData) return location.menuSchedules
		// API returns ref+name only; merge with bootstrap for full hours, fall back to empty hours when absent (Ember available-menu-schedule parity).
		// Empty result means the API excluded all schedules — do not fall back to bootstrap.
		return availableSchedulesData.schedules.map((s) => {
			const full = location.menuSchedules.find((ls) => ls.ref === s.ref)
			return full ?? { ref: s.ref, name: s.name, hours: [], duringOffHours: false }
		})
	}, [isDeferred, isDineIn, availableSchedulesData, location])

	function handleClearSearch() {
		clearSearchRef.current?.()
		setSearchTerm('')
	}

	const isStoreClosureActive = outletContext?.isStoreClosureActive ?? false

	const childContext: MenuLayoutContext = {
		location,
		scheduleId,
		searchTerm,
		onClearSearch: handleClearSearch,
		isStoreClosureActive,
	}

	return (
		<>
			{/* Outer grid — CartSidebar starts from the top alongside the header (matches Figma desktop) */}
			<LayoutGrid data-testid="menu-layout-grid">
				{/* Left column: header → nav → page content */}
				<div>
					<StickyHeader ref={menuHeaderRef} isCompact={isCompact}>
						<MenuHeader
							location={outletContext?.location}
							schedules={headerSchedules}
							isCompact={isCompact}
						/>
						<CategoryNav
							scheduleId={scheduleId}
							onSearch={(term) => setSearchTerm(term.trim())}
							clearSearchRef={clearSearchRef}
							searchTerm={searchTerm}
						/>
					</StickyHeader>
					<MainContent>
						{/* MenuPage is always mounted so items render regardless of child route.
						    Overlay routes (order-settings, add-item, etc.) render via Outlet on top. */}
						<MenuPage
							scheduleId={scheduleId}
							searchTerm={searchTerm}
							onClearSearch={handleClearSearch}
							currency={location?.currency}
						/>
						<Outlet context={childContext} />
					</MainContent>
				</div>

				{/* Right column: sticky wrapper constrains the cart to viewport height so
				    only the items list scrolls — header and footer stays fixed. */}
				<CartSidebarSticky>
					<CartSidebar
						scheduleId={scheduleId}
						timeZoneOffset={location?.timeZoneOffset}
					/>
				</CartSidebarSticky>
			</LayoutGrid>

			{/* Tablet floating bottom bar — visible at md (721px–1024px), hidden at lg where sidebar takes over */}
			<CartBottomBar scheduleId={scheduleId} timeZoneOffset={location?.timeZoneOffset} />

			{isStoreClosureActive && location && (
				<StoreClosed
					locationName={location.name}
					address={[location.addressLine1, location.city, location.state]
						.filter(Boolean)
						.join(', ')}
					message={location.closureReason}
				/>
			)}
		</>
	)
}
